<script setup lang="ts">
import store from "@/store";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  loadStripe,
  type Stripe,
  type StripeElements,
  type StripePaymentElement,
} from "@stripe/stripe-js";

import { toast } from "@/common/utils/toast";
import { maskCPF, maskCNPJ } from "@/common/utils/mask";

import { planGet } from "@/common/api/rest/plan";
import { checkoutPlan } from "@/common/api/rest/payment/checkout";
import { userAccountByMember } from "@/common/api/rest/user";

import type { PlanType } from "@/common/types/plan/plan";

import { DefaultTheme } from "@/common/themes";
import { baseInput, baseSelect } from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const account = ref<any>(null);
const plan = ref<PlanType | null>(null);
const isLoading = ref(true);
const isProcessing = ref(false);

const terms = ref<any[]>([]);
const error = ref<{ [key: string]: string }>({});

const stripe = ref<Stripe | null>(null);
const elements = ref<StripeElements | null>(null);
const paymentElement = ref<StripePaymentElement | null>(null);
const clientSecret = ref("");

const DEFAULT_PAYLOAD = {
  name: "",
  email: "",
  doc_type: "br_cpf",
  doc_number: "",
};

const payload = ref<any>({ ...DEFAULT_PAYLOAD });
const frequency = ref("MONTHLY");

const docTypeOptions = computed(() => [
  { option: "CPF", value: "br_cpf" },
  { option: "CNPJ", value: "br_cnpj" },
]);

const frequencyOptions = computed(() => ({
  MONTHLY: "Mensal",
  SEMESTER: "Semestral",
  YEARLY: "Anual",
}));

const currencyOptions = computed(() => ({
  BRL: "R$",
  USD: "$",
  EUR: "€",
}));

function documentMask(value: any) {
  if (payload.value.doc_type === "br_cpf") return maskCPF(value);

  if (payload.value.doc_type === "br_cnpj") return maskCNPJ(value);

  return value;
}

function changeType(type: any) {
  payload.value.doc_type = type;
  payload.value.doc_number = documentMask(payload.value.doc_number);
}

async function initStripe() {
  const pk = process.env.VUE_APP_STRIPE_PUBLIC;
  if (!pk) {
    toast({
      message: "Erro ao carregar chave pública do Stripe",
      type: "error",
    });
    return;
  }

  stripe.value = await loadStripe(pk);

  if (!stripe.value) {
    toast({ message: "Erro ao carregar instância do Stripe", type: "error" });
  }
}

async function fetchAccount() {
  try {
    const response = await userAccountByMember(store.getters.user?.member_id);
    if (response.success) {
      account.value = response.data;
    } else {
      toast({ message: response.message, type: "error" });
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  }
}

async function fetchPlan() {
  const planId = route.params.plan as string;
  if (!planId) {
    toast({ message: t("plan.errors.plan_not_found"), type: "error" });
    router.push("/plans");
    return;
  }

  try {
    const accountId = account.value?.account_id;
    const response = await planGet(accountId, planId);
    if (response.success) {
      plan.value = response.data;
      terms.value = (response.data?.terms || []).map((item: any) => ({
        ...item,
        accepted: false,
        url: `https://www.segform.com.br/pt/${item.code.toLowerCase()}`,
      }));
    } else {
      toast({ message: response.message, type: "error" });
      router.push("/plans");
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  }
}

/**
 * Desmonta o Payment Element e limpa o clientSecret,
 * voltando o formulário para o estado "Continuar".
 */
function resetPayment() {
  if (paymentElement.value) {
    try {
      paymentElement.value.unmount();
    } catch (_) {
      /* unmount pode falhar se já foi desmontado */
    }
    paymentElement.value = null;
  }
  elements.value = null;
  clientSecret.value = "";
}

/**
 * Se o usuário desmarcar qualquer termo depois que o cartão já foi liberado,
 * removemos o cartão e obrigamos ele a marcar tudo de novo.
 */
watch(
  terms,
  () => {
    if (!clientSecret.value) return;

    const allAccepted = terms.value.every((item) => item.accepted);
    if (!allAccepted) {
      resetPayment();
    }
  },
  { deep: true },
);

async function preparePayment() {
  if (!plan.value || !stripe.value) {
    toast({ message: "Stripe não inicializado corretamente", type: "error" });
    return false;
  }

  try {
    const price = plan.value.prices.find(
      (item) => item.frequency === frequency.value,
    );

    // 1. inicializa o checkout do plano na API do Stripe
    const checkoutResponse = await checkoutPlan(account.value.account_id, {
      member_id: store.getters.user.member_id,
      plan_id: plan.value.plan_id,
      price_id: price.price_id,
      name: payload.value.name,
      email: payload.value.email,
      doc_type: payload.value.doc_type,
      doc_number: payload.value.doc_number,
    });

    if (!checkoutResponse.success) {
      throw new Error(checkoutResponse.message);
    }

    clientSecret.value = checkoutResponse.data.client_secret;

    if (paymentElement.value) {
      paymentElement.value.unmount();
      paymentElement.value = null;
    }

    // Instancia Elements passando o clientSecret obrigatório
    elements.value = stripe.value.elements({
      clientSecret: clientSecret.value,
      appearance: { theme: "stripe" },
    });

    await nextTick();
    paymentElement.value = elements.value.create("payment");
    paymentElement.value.mount("#payment-element");

    return true;
  } catch (error: any) {
    toast({ message: error.message || t("error.default"), type: "error" });
    return false;
  }
}

async function confirmPayment() {
  if (!stripe.value || !elements.value || !clientSecret.value) return;

  try {
    isProcessing.value = true;

    const { error } = await stripe.value.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: `${window.location.origin}/signs`,
        payment_method_data: {
          billing_details: {
            name: payload.value.name,
            email: payload.value.email,
          },
        },
      },
    });

    if (error) {
      toast({ message: error.message || t("error.default"), type: "error" });
    }
  } catch (error: any) {
    toast({ message: error.message || t("error.default"), type: "error" });
  } finally {
    isProcessing.value = false;
  }
}

function valid() {
  return (
    payload.value.name &&
    payload.value.email &&
    payload.value.doc_number &&
    terms.value.every((item) => item.accepted)
  );
}

async function handleSubmit() {
  if (!valid()) {
    toast({ message: "Campos obrigatórios não preenchidos", type: "error" });
    return;
  }

  if (!clientSecret.value) {
    isProcessing.value = true;
    await preparePayment();
    isProcessing.value = false;
  } else {
    // 2ª etapa: Confirma o pagamento enviado no Element
    await confirmPayment();
  }
}

function getCurrency(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return currencyOptions.value[price.currency];
  }

  return " -- ";
}

function getPrice(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return (price.price / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return " -- ";
}

function getDescription(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return price.description;
  }

  return " -- ";
}

function goBack() {
  router.push("/plans");
}

onMounted(async () => {
  isLoading.value = true;
  await fetchAccount();
  await initStripe();
  await fetchPlan();
  isLoading.value = false;

  frequency.value = route.query.frequency as string;
});
</script>

<template>
  <default-theme menu-active="plans" :account-status="account?.status">
    <section class="checkout-page">
      <div v-if="isLoading" class="loading">{{ t("common.loading") }}</div>

      <div v-else-if="plan" class="checkout-container">
        <header class="checkout-header">
          <h1>{{ t("checkout.labels.title") }}</h1>
          <div class="plan-summary">
            <span>{{ plan.name }}</span>
            <strong>
              {{ getCurrency(plan) }} {{ getPrice(plan) }} /
              {{ frequencyOptions[frequency] }}
            </strong>
          </div>
        </header>

        <form @submit.prevent="handleSubmit" class="checkout-form">
          <!-- Seção de dados do cliente -->
          <section class="customer-section">
            <h2>{{ t("checkout.labels.customer") }}</h2>
            <base-input v-model="payload.name" :value="payload.name">
              <template #label> {{ t("common.labels.name") }} * </template>
            </base-input>

            <base-input v-model="payload.email" :value="payload.email">
              <template #label> {{ t("common.labels.email") }} * </template>
            </base-input>

            <base-select
              v-model="payload.doc_type"
              :value="payload.doc_type"
              :options="docTypeOptions"
              :is-empty="false"
              @change="changeType"
            >
              <template #label>
                {{ t("common.labels.document_type") }} *
              </template>
            </base-select>

            <base-input
              v-model="payload.doc_number"
              :value="payload.doc_number"
              :mask="documentMask"
            >
              <template #label> {{ t("common.labels.number") }} * </template>
            </base-input>
          </section>

          <div class="terms">
            <template v-for="term in terms" :key="term.term_id">
              <label class="terms-checkbox">
                <input
                  v-model="term.accepted"
                  type="checkbox"
                  class="checkbox-input"
                  :class="{ 'has-error': error[term.term_id] }"
                />
                <span class="checkbox-text">
                  Li e concordo com
                  <a :href="term.url" target="_blank">{{ term.name }}</a>
                </span>
              </label>
            </template>
          </div>

          <!-- Aviso: falta marcar todos os termos -->
          <p
            v-if="terms.length && !terms.every((item) => item.accepted) && !clientSecret"
            class="terms-hint"
          >
            Marque todos os termos para liberar o pagamento.
          </p>

          <!-- Seção de cartão (renderizada pelo Stripe quando o clientSecret existir) -->
          <section v-show="clientSecret" class="payment-section">
            <h2>{{ t("checkout.labels.card") }}</h2>
            <div id="payment-element" class="payment-element"></div>
          </section>

          <button
            type="submit"
            class="btn btn-primary btn-lg"
            :disabled="isProcessing"
          >
            {{
              isProcessing
                ? t("checkout.labels.processing")
                : clientSecret
                  ? t("checkout.labels.pay")
                  : t("checkout.labels.continue")
            }}
          </button>
        </form>

        <button class="btn back" @click="goBack">
          {{ t("common.labels.back") }}
        </button>
      </div>
    </section>
  </default-theme>
</template>

<style lang="scss" scoped>
.checkout-page {
  min-height: 100vh;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
}

.loading {
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #555;
}

.checkout-container {
  display: table;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  max-width: 600px;
  width: 100%;
  padding: 2rem;
}

.checkout-header {
  margin-bottom: 2rem;

  h1 {
    font-size: 1.8rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  .plan-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1rem;
    font-size: 1.1rem;

    span {
      color: #334155;
    }

    strong {
      color: #1e293b;
      font-size: 1.3rem;
    }
  }
}

.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  .customer-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-bottom: 1rem;

    label {
      font-size: 0.9rem;
      font-weight: 500;
      color: #475569;
    }

    input {
      padding: 0.8rem;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 1rem;
      transition:
        border-color 0.2s,
        box-shadow 0.2s;

      &:focus {
        outline: none;
        border-color: #4a90e2;
        box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15);
      }
    }
  }

  .payment-element {
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0.8rem;
    background: #fff;
    margin-bottom: 1rem;
  }
}

.btn {
  padding: 1rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.back {
    width: 70%;
    margin-top: 2rem;
    margin-inline: auto;
  }

  &-primary {
    background: #1e293b;
    color: #fff;
    padding-block: 2rem;

    &:hover:not(:disabled) {
      background: #0f172a;
      transform: translateY(-1px);
    }
  }
}

.terms {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .terms-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 1rem;

    .checkbox-input {
      margin-top: 0.3rem;
      accent-color: var(--primary);

      // Quando houver erro
      &.has-error {
        accent-color: red;
        outline: 2px solid red;
        outline-offset: 2px;
        border-radius: 2px;
      }
    }

    .checkbox-text {
      a {
        color: var(--primary);
        text-decoration: none;
        font-weight: 600;
      }
    }
  }

  .error-message {
    width: 100%;
    text-align: right;
    color: red;
    font-size: 0.8rem;
  }
}

.terms-hint {
  font-size: 0.9rem;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
}

@media (max-width: 480px) {
  .checkout-container {
    padding: 1.5rem;
  }

  .plan-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
